import {ReactNode, useState, useEffect} from 'react';
import {Link as RouterLink, useLocation} from 'react-router-dom';
import {AppBar, Box, Divider, Drawer, FormControlLabel, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Switch, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,} from '@mui/material';
import {ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, VisibilityOff as VisibilityOffIcon, Help as HelpIcon, Menu as MenuIcon, DarkMode as DarkModeIcon, LightMode as LightModeIcon} from '@mui/icons-material';
import CompanyBadge from './CompanyBadge';
import FeedbackWidget from './FeedbackWidget';
import UserMenu from './UserMenu';
import {menuFor, menuItems} from './navigation';
import {useColorMode} from '../contexts/ColorModeContext';
import {usePlatformRole} from '../auth/AuthContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface LayoutProps {
    children: ReactNode;
    onTourRequest?: () => void;
}

// Pages reached from the app bar or the user menu rather than the sidebar (SB-321).
const otherPages = [
    {path: '/company', title: 'Firma', subtitle: 'Medlemmene og rollene deres i firmaet du jobber i'},
];

export default function Layout({children, onTourRequest}: LayoutProps) {
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [menuOpen, setMenuOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [hideUnused, setHideUnused] = useState(true);
    const isPlatform = usePlatformRole();
    const shown = menuFor(isPlatform, hideUnused);

    const visibleItemCount = shown.length;

    // Inside a project (drawings/facts/compliance, or the create flow) the
    // sidebar steals width the user wants for the canvas — collapse it. The
    // bare list (`/ude/projects`) and other top-level pages keep it expanded.
    const inProjectDetail = /^\/ude\/projects\/.+/.test(location.pathname);

    // Auto-manage the sidebar: collapse inside a project or when only 1 nav
    // item is visible; expand otherwise. Re-applies on navigation, so the
    // manual chevron toggle holds until the next route change.
    useEffect(() => {
        if (isMobile) return;
        setMenuOpen(visibleItemCount > 1 && !inProjectDetail);
    }, [visibleItemCount, isMobile, inProjectDetail]);

    const currentDrawerWidth = menuOpen ? drawerWidth : collapsedDrawerWidth;

    const {toggleColorMode, mode} = useColorMode();

    // Get current page info based on route
    const getCurrentMenuItem = () => {
        const exactMatch = menuItems.find(item => item.path === location.pathname);
        if (exactMatch) return exactMatch;

        const prefixMatch = menuItems.find(item => location.pathname.startsWith(item.path + '/') || location.pathname === item.path);
        if (prefixMatch) return prefixMatch;

        return otherPages.find(page => page.path === location.pathname) ?? null;
    };

    const currentItem = getCurrentMenuItem();

    // On mobile, always show expanded menu text
    const showMenuText = isMobile || menuOpen;

    const handleNavClick = () => {
        if (isMobile) setMobileOpen(false);
    };

    // Shared drawer content
    const drawerContent = (
        <>
            <Toolbar>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    {showMenuText && (
                        <Typography variant="h6" component="div" noWrap>
                            Menu
                        </Typography>
                    )}
                    {!isMobile && (
                        <IconButton onClick={() => setMenuOpen(!menuOpen)} size="small" aria-label={menuOpen ? 'Collapse menu' : 'Expand menu'}>
                            {menuOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    )}
                </Box>
            </Toolbar>
            <List sx={{flexGrow: 1}}>
                {shown.map((item) => (
                    <ListItem key={item.path} disablePadding>
                        <ListItemButton
                            component={RouterLink}
                            to={item.path}
                            selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                            onClick={handleNavClick}
                            sx={{
                                minHeight: 48,
                                justifyContent: showMenuText ? 'initial' : 'center',
                                px: 2.5,
                                textDecoration: 'none',
                                color: 'inherit',
                                borderLeft: '3px solid transparent',
                                '&.Mui-selected': {
                                    borderLeftColor: 'primary.main',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: showMenuText ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            {showMenuText && <ListItemText primary={item.text}/>}
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider/>
            <Box sx={{p: showMenuText ? 1.5 : 0.5, display: 'flex', justifyContent: 'center'}}>
                {showMenuText ? (
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={hideUnused}
                                onChange={(e) => setHideUnused(e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Hide unused</Typography>}
                        sx={{m: 0}}
                    />
                ) : (
                    <IconButton
                        size="small"
                        onClick={() => setHideUnused(!hideUnused)}
                        sx={{color: hideUnused ? 'text.secondary' : 'primary.main'}}
                        title="Toggle hidden menu items"
                    >
                        <VisibilityOffIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>
        </>
    );

    return (
        <Box sx={{display: 'flex'}}>
            {/* Skip to content link for keyboard navigation */}
            <Box
                component="a"
                href="#main-content"
                sx={{
                    position: 'absolute',
                    left: '-9999px',
                    zIndex: 9999,
                    padding: '1rem',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:focus': {
                        left: 0,
                        top: 0,
                    },
                }}
            >
                Skip to content
            </Box>
            <AppBar
                position="fixed"
                sx={{
                    width: isMobile ? '100%' : `calc(100% - ${currentDrawerWidth}px)`,
                    ml: isMobile ? 0 : `${currentDrawerWidth}px`,
                    transition: isMobile ? 'none' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <Toolbar>
                    {isMobile && (
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open navigation menu"
                            sx={{mr: 1}}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box sx={{display: 'flex', alignItems: 'baseline', minWidth: 0, flex: 1}}>
                        <Typography variant="h6" noWrap component="div" sx={{lineHeight: 1.2}}>
                            {currentItem?.title || 'SuccessBuilder'}
                        </Typography>
                        {currentItem?.subtitle && (
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{ml: 2, opacity: 0.8, fontStyle: 'italic', lineHeight: 1, display: {xs: 'none', lg: 'block'}}}
                            >
                                {currentItem.subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
                        <IconButton
                            color="inherit"
                            onClick={toggleColorMode}
                            size="small"
                            aria-label="Toggle color mode"
                            sx={{mr: 1}}
                        >
                            {mode === 'light' ? <DarkModeIcon fontSize="small"/> : <LightModeIcon fontSize="small"/>}
                        </IconButton>
                    </Tooltip>
                    {onTourRequest && (
                        <Tooltip title="Take a tour">
                            <IconButton
                                color="inherit"
                                onClick={onTourRequest}
                                size="small"
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'inherit',
                                    opacity: 0.4,
                                    width: 32,
                                    height: 32,
                                    '&:hover': {opacity: 0.8},
                                }}
                            >
                                <HelpIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <CompanyBadge/>
                    <UserMenu/>
                </Toolbar>
            </AppBar>
            {/* Mobile: Temporary overlay drawer */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{keepMounted: true}}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            },
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                /* Desktop: Permanent collapsible drawer */
                (<Drawer
                    variant="permanent"
                    anchor="left"
                    sx={{
                        width: currentDrawerWidth,
                        flexShrink: 0,
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiDrawer-paper': {
                            width: currentDrawerWidth,
                            boxSizing: 'border-box',
                            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            overflowX: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            },
                    }}
                >
                    {drawerContent}
                </Drawer>)
            )}
            <Box
                component="main"
                id="main-content"
                sx={{
                    flexGrow: 1,
                    bgcolor: 'background.default',
                    p: {xs: 1.5, sm: 2, md: 3},
                    mt: 8,
                    minWidth: 0,
                }}
            >
                {/* Visually-hidden h1 for screen readers — anchors heading hierarchy */}
                {currentItem && (
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            whiteSpace: 'nowrap',
                            borderWidth: 0,
                        }}
                    >
                        {currentItem.title}
                    </Typography>
                )}
                {children}
            </Box>
            <FeedbackWidget/>
        </Box>
    );
}
